// src/api/administrationsApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

export const administrationsApi = createApi({
  reducerPath: "administrationsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["Articles", "Article", "Sales"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    // NEW — create article
    createArticle: builder.mutation({
      query: (body) => ({
        url: "administration/articles",
        method: "POST",
        body,
      }),
      // Invalidate list so queries refetch
      invalidatesTags: [{ type: "Articles", id: "LIST" }],
    }),

    // NEW — update article
    updateArticle: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `administration/articles/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "Article", id: arg.id },
        { type: "Articles", id: "LIST" },
      ],
    }),

    addArticleQuantity: builder.mutation({
      query: ({ articleId, quantity }) => ({
        url: "administration/add-quantity",
        method: "POST",
        body: { article_id: articleId, added_quantity: quantity },
      }),
      invalidatesTags: (res, _err, arg) => [
        { type: "Article", id: arg.articleId },
        { type: "Articles", id: "LIST" },
      ],
    }),

    getArticlesByType: builder.query({
      query: (type) => ({ url: "administration/articles", params: { type } }),
      transformResponse: (res) => (res?.success ? res.data : res ?? []),
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((a) => ({ type: "Article", id: a.id })),
              { type: "Articles", id: "LIST" },
            ]
          : [{ type: "Articles", id: "LIST" }],
    }),
    getSales: builder.query({
      query: () => "administration/sales",
      transformResponse: (res) => (res?.success ? res.data : res ?? []),
      providesTags: [{ type: "Sales", id: "LIST" }],
    }),
    recordSale: builder.mutation({
      query: (body) => ({
        url: "administration/sales",
        method: "POST",
        body,
      }),
      // sales list will refresh automatically where used
      invalidatesTags: [{ type: "Sales", id: "LIST" }],
    }),
  }),
});

export const {
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useAddArticleQuantityMutation,
  useGetArticlesByTypeQuery,
  useLazyGetArticlesByTypeQuery,
  useGetSalesQuery,
  useLazyGetSalesQuery,
  useRecordSaleMutation,
} = administrationsApi;
